import {
    Box,
    Button,
    ButtonBase,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import _, { get } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import GlobalNotification from '../../component/common/snackbar';
import { ReactComponent as DeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import PermissionSettingsListPanel from './permission-settings-list-panel.component';
import PermissionSettingsMatrix from './permission-settings-matrix.component';
import { PERMISSION_ALL_KEYS } from './permission-settings-quick-presets';
import { normalizeRowForForm } from './permission-normalize.utils';
import type { EmployeePermissionClassRow } from './permission-settings.types';
import './scss/permission-settings.page.scss';

function isApiError(res: unknown): res is { error: true } {
    return Boolean(res && typeof res === 'object' && 'error' in res && (res as { error?: boolean }).error);
}

type RightPanelDraft = {
    employeeName: string;
    formState: EmployeePermissionClassRow;
};

/** 设计稿岗位名称旁编辑图标（fill 随 `currentColor` / 主题色） */
function PermissionSettingsNameEditIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
            <path
                d="M18.9379 4.60844L19.3916 5.06219C19.7441 5.41469 19.7441 5.98469 19.3916 6.33344L18.3004 7.42844L16.5716 5.69969L17.6629 4.60844C18.0154 4.25594 18.5854 4.25594 18.9341 4.60844H18.9379ZM10.2679 12.0072L15.3004 6.97094L17.0291 8.69969L11.9929 13.7322C11.8841 13.8409 11.7491 13.9197 11.6029 13.9609L9.40914 14.5872L10.0354 12.3934C10.0766 12.2472 10.1554 12.1122 10.2641 12.0034L10.2679 12.0072ZM16.3916 3.33719L8.99289 10.7322C8.66664 11.0584 8.43039 11.4597 8.30664 11.8984L7.23414 15.6484C7.14414 15.9634 7.23039 16.3009 7.46289 16.5334C7.69539 16.7659 8.03289 16.8522 8.34789 16.7622L12.0979 15.6897C12.5404 15.5622 12.9416 15.3259 13.2641 15.0034L20.6629 7.60844C21.7166 6.55469 21.7166 4.84469 20.6629 3.79094L20.2091 3.33719C19.1554 2.28344 17.4454 2.28344 16.3916 3.33719ZM5.70039 4.79969C3.87789 4.79969 2.40039 6.27719 2.40039 8.09969V18.2997C2.40039 20.1222 3.87789 21.5997 5.70039 21.5997H15.9004C17.7229 21.5997 19.2004 20.1222 19.2004 18.2997V14.0997C19.2004 13.6009 18.7991 13.1997 18.3004 13.1997C17.8016 13.1997 17.4004 13.6009 17.4004 14.0997V18.2997C17.4004 19.1284 16.7291 19.7997 15.9004 19.7997H5.70039C4.87164 19.7997 4.20039 19.1284 4.20039 18.2997V8.09969C4.20039 7.27094 4.87164 6.59969 5.70039 6.59969H9.90039C10.3991 6.59969 10.8004 6.19844 10.8004 5.69969C10.8004 5.20094 10.3991 4.79969 9.90039 4.79969H5.70039Z"
                fill="currentColor"
            />
        </svg>
    );
}

/**
 * 云 RBP 员工管理 — 权限设置：左侧员工等级列表，右侧权限勾选与保存（业务与 RBP2 `employee-permission-setting.page.tsx` 一致；布局 Figma 1756:21285 / 1756:21200）
 */
export default function PermissionSettingsPage() {
    const [list, setList] = useState<EmployeePermissionClassRow[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [formState, setFormState] = useState<EmployeePermissionClassRow>({});
    const [employeeName, setEmployeeName] = useState('');
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [addName, setAddName] = useState('');
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [saveSubmitting, setSaveSubmitting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    /** 快速配置：当前草稿权限所「套用」的源岗位 `employee_class_id`（来自列表，非写死预设） */
    const [quickTemplateSourceId, setQuickTemplateSourceId] = useState<number | null>(null);
    /** 岗位名称：默认只读，点铅笔进入编辑 */
    const [nameEditMode, setNameEditMode] = useState(false);
    const selectedIdRef = useRef<number | null>(null);
    selectedIdRef.current = selectedId;
    const draftRef = useRef<RightPanelDraft | null>(null);
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const selectedRow = useMemo(
        () => list.find((r) => r.employee_class_id === selectedId) ?? null,
        [list, selectedId],
    );

    /** 可作快速套用的其他岗位（排除当前选中） */
    const quickTemplateRows = useMemo(
        () =>
            list.filter(
                (r) => r.employee_class_id != null && r.employee_class_id !== selectedId,
            ),
        [list, selectedId],
    );

    const loadList = useCallback(async (preferredId?: number | null) => {
        setLoading(true);
        try {
            const res = await apiManage_rbp.get_employee_permission_setting();
            if (isApiError(res)) {
                return;
            }
            if (!Array.isArray(res)) {
                setList([]);
                setSelectedId(null);
                setFormState({});
                setEmployeeName('');
                setQuickTemplateSourceId(null);
                setNameEditMode(false);
                draftRef.current = null;
                return;
            }
            const rows = res as EmployeePermissionClassRow[];
            setList(rows);
            const keepId = preferredId !== undefined ? preferredId : selectedIdRef.current;
            const active =
                (keepId != null ? rows.find((r) => r.employee_class_id === keepId) : undefined) ?? rows[0];
            if (active?.employee_class_id != null) {
                const normalized = normalizeRowForForm(active);
                const name = String(active.employee_class_name ?? '');
                setSelectedId(active.employee_class_id);
                setFormState(normalized);
                setEmployeeName(name);
                setQuickTemplateSourceId(null);
                setNameEditMode(false);
                draftRef.current = { employeeName: name, formState: _.cloneDeep(normalized) };
            } else {
                setSelectedId(null);
                setFormState({});
                setEmployeeName('');
                setQuickTemplateSourceId(null);
                setNameEditMode(false);
                draftRef.current = null;
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadList();
    }, [loadList]);

    const applyRow = useCallback((item: EmployeePermissionClassRow) => {
        const id = item.employee_class_id;
        if (id == null) {
            return;
        }
        const normalized = normalizeRowForForm(item);
        const name = String(item.employee_class_name ?? '');
        setSelectedId(id);
        setFormState(normalized);
        setEmployeeName(name);
        setQuickTemplateSourceId(null);
        setNameEditMode(false);
        draftRef.current = { employeeName: name, formState: _.cloneDeep(normalized) };
    }, []);

    useEffect(() => {
        if (nameEditMode) {
            queueMicrotask(() => nameInputRef.current?.focus());
        }
    }, [nameEditMode]);

    const onToggleCheckbox = useCallback((key: string, checked: boolean) => {
        setQuickTemplateSourceId(null);
        setFormState((prev) => ({
            ...prev,
            [key]: checked ? 1 : 0,
        }));
    }, []);

    const handleCancel = useCallback(() => {
        const snap = draftRef.current;
        if (!snap) {
            return;
        }
        setEmployeeName(snap.employeeName);
        setFormState(_.cloneDeep(snap.formState));
        setQuickTemplateSourceId(null);
        setNameEditMode(false);
    }, []);

    /** 取消：先恢复草稿，再关闭右侧（取消选中） */
    const handleCancelCloseRight = useCallback(() => {
        handleCancel();
        setSelectedId(null);
        draftRef.current = null;
    }, [handleCancel]);

    const applyQuickTemplateFromRow = useCallback((source: EmployeePermissionClassRow) => {
        const sid = source.employee_class_id;
        if (sid == null) {
            return;
        }
        const normalized = normalizeRowForForm(source);
        setQuickTemplateSourceId(sid);
        setFormState((prev) => {
            const next = { ...prev };
            for (const k of PERMISSION_ALL_KEYS) {
                const v = get(normalized, k, 0);
                next[k] = Number(v) === 1 ? 1 : 0;
            }
            return next;
        });
    }, []);

    const onSave = useCallback(async () => {
        const id = formState.employee_class_id;
        if (id == null) {
            return;
        }
        if (!_.trim(employeeName)) {
            GlobalNotification.error(i18n.t('staff_setting_page_please_fill_in_the_employee_name'));
            return;
        }
        setSaveSubmitting(true);
        try {
            const body = _.cloneDeep(formState) as Record<string, unknown>;
            /** extensions 为真值时置 0，否则置 1（与接口约定一致） */
            body.extensions = body.extensions ? 0 : 1;
            body.employee_class_name = _.trim(employeeName);
            body.payment = body.payment === 1 ? 5 : 0;
            const res = await apiManage_rbp.patch_employee_permission_setting(id, body);
            if (!isApiError(res)) {
                GlobalNotification.success(i18n.t('google_text_edit_succeed'));
                await loadList();
            }
        } finally {
            setSaveSubmitting(false);
        }
    }, [employeeName, formState, loadList]);

    const submitAdd = useCallback(async () => {
        if (!_.trim(addName)) {
            GlobalNotification.warning(i18n.t('staff_setting_page_enter_type_name'));
            return;
        }
        setAddSubmitting(true);
        try {
            const res = await apiManage_rbp.post_employee_permission_setting({ employee_class_name: _.trim(addName) });
            if (!isApiError(res)) {
                setAddOpen(false);
                setAddName('');
                await loadList();
            }
        } finally {
            setAddSubmitting(false);
        }
    }, [addName, loadList]);

    const confirmDelete = useCallback(async () => {
        if (deleteTargetId == null) {
            setDeleteOpen(false);
            return;
        }
        setDeleteSubmitting(true);
        try {
            const res = await apiManage_rbp.delete_employee_permission_setting(deleteTargetId);
            if (isApiError(res)) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            setDeleteOpen(false);
            setDeleteTargetId(null);
            await loadList();
        } finally {
            setDeleteSubmitting(false);
        }
    }, [deleteTargetId, loadList]);

    return (
        <Box className="permission-settings-page">
            <Box className="permission-settings-page__main">
                <PermissionSettingsListPanel
                    list={list}
                    selectedId={selectedId}
                    loading={loading}
                    onSelect={applyRow}
                    onAdd={() => {
                        setAddName('');
                        setAddOpen(true);
                    }}
                />

                {selectedRow != null && (
                    <Box className="permission-settings-page__right-panel">
                        <Box className="permission-settings-page__right-header">
                            <Box className="permission-settings-page__right-header-name-row">
                                {!nameEditMode ? (
                                    <Typography
                                        component="div"
                                        className="permission-settings-page__right-name-readonly"
                                        title={employeeName}
                                    >
                                        {employeeName.trim() ? employeeName : '-'}
                                    </Typography>
                                ) : (
                                    <TextField
                                        className="permission-settings-page__right-name-field"
                                        variant="standard"
                                        value={employeeName}
                                        onChange={(e) => {
                                            setEmployeeName(e.target.value);
                                            setQuickTemplateSourceId(null);
                                        }}
                                        onBlur={() => setNameEditMode(false)}
                                        inputRef={nameInputRef}
                                        InputProps={{
                                            disableUnderline: false,
                                        }}
                                    />
                                )}
                                <IconButton
                                    type="button"
                                    size="small"
                                    className="permission-settings-page__right-name-edit-btn"
                                    onMouseDown={(e) => {
                                        if (nameEditMode) e.preventDefault();
                                    }}
                                    onClick={() => {
                                        if (nameEditMode) setNameEditMode(false);
                                        else setNameEditMode(true);
                                    }}
                                >
                                    <PermissionSettingsNameEditIcon />
                                </IconButton>
                            </Box>
                            <ButtonBase
                                type="button"
                                className="permission-settings-page__right-delete"
                                onClick={() => {
                                    if (selectedId != null) {
                                        setDeleteTargetId(selectedId);
                                        setDeleteOpen(true);
                                    }
                                }}
                            >
                                <DeleteForeverIcon width={32} height={32} style={{ color: 'var(--func-error)' }} aria-hidden />
                                <Typography component="span" className="permission-settings-page__right-delete-label">
                                    {i18n.t('permission_settings_delete_role')}
                                </Typography>
                            </ButtonBase>
                        </Box>
                        <Divider className="permission-settings-page__right-divider" />

                        {quickTemplateRows.length > 0 && (
                            <>
                                <Typography component="h3" className="permission-settings-page__right-subtitle">
                                    {i18n.t('permission_settings_quick_config')}
                                </Typography>
                                <Box className="permission-settings-page__quick-presets">
                                    {quickTemplateRows.map((row) => {
                                        const id = row.employee_class_id as number;
                                        const selectedChip = quickTemplateSourceId === id;
                                        const label = String(row.employee_class_name ?? '').trim() || '-';
                                        return (
                                            <ButtonBase
                                                key={id}
                                                type="button"
                                                className={
                                                    selectedChip
                                                        ? 'permission-settings-page__quick-preset-chip permission-settings-page__quick-preset-chip--selected'
                                                        : 'permission-settings-page__quick-preset-chip'
                                                }
                                                onClick={() => applyQuickTemplateFromRow(row)}
                                            >
                                                <Typography
                                                    component="span"
                                                    className="permission-settings-page__quick-preset-chip-label"
                                                    title={label}
                                                >
                                                    {label}
                                                </Typography>
                                            </ButtonBase>
                                        );
                                    })}
                                </Box>
                            </>
                        )}

                        <Typography component="h3" className="permission-settings-page__right-subtitle permission-settings-page__right-subtitle--perm">
                            {i18n.t('permission_settings_section_role_permissions')}
                        </Typography>
                        <PermissionSettingsMatrix formState={formState} onToggle={onToggleCheckbox} />

                        <Box className="permission-settings-page__right-footer">
                            <ButtonBase
                                type="button"
                                className="permission-settings-page__right-footer-cancel"
                                disabled={saveSubmitting}
                                onClick={handleCancelCloseRight}
                            >
                                <Typography component="span">{i18n.t('job_settings_cancel')}</Typography>
                            </ButtonBase>
                            <LoadingOverlayButton
                                type="button"
                                className="permission-settings-page__right-footer-save"
                                variant="contained"
                                loading={saveSubmitting}
                                loadingIndicatorSize={22}
                                startIcon={<SaveIcon width={24} height={24} aria-hidden />}
                                onClick={() => void onSave()}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        color: 'var(--text-light)',
                                        fontSize: 20,
                                        fontWeight: 700,
                                        lineHeight: '24px',
                                    }}
                                >
                                    {i18n.t('permission_settings_save_changes')}
                                </Typography>
                            </LoadingOverlayButton>
                        </Box>
                    </Box>
                )}
            </Box>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{i18n.t('staff_permission_page_operation_text')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, width: '100%' }}>
                        <FormFieldCell labelKey="staff_permission_page_name">
                            <CommonInput
                                inputType="INPUT"
                                hiddenLabel
                                fullWidth
                                size="small"
                                variant="outlined"
                                required
                                regularType="required"
                                value={addName}
                                onChange={(e) => setAddName(e.target.value)}
                            />
                        </FormFieldCell>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>{i18n.t('google_text_public_button_cancel')}</Button>
                    <LoadingOverlayButton
                        type="button"
                        variant="contained"
                        loading={addSubmitting}
                        loadingIndicatorSize={22}
                        onClick={() => void submitAdd()}
                    >
                        {i18n.t('google_text_confirm')}
                    </LoadingOverlayButton>
                </DialogActions>
            </Dialog>

            <AlertDialog
                open={deleteOpen}
                variant="delete"
                title={i18n.t('dinner_table_page_confirm_delete')}
                desc={i18n.t('dinner_table_page_whether_confirm_delete')}
                size="xs"
                confirmLoading={deleteSubmitting}
                onCancel={() => setDeleteOpen(false)}
                onConfirm={() => void confirmDelete()}
            />
        </Box>
    );
}
